<script setup lang="ts">
import { computed } from 'vue'
import {
  FIELD_SOURCES,
  FIELD_TYPES,
  MISSING_CODES,
  type ClinicCrfCondition,
  type ClinicCrfField,
  type ClinicCrfFieldPresentation,
  type ClinicCrfFieldType,
  type ClinicCrfMissingCode,
} from '@clinic-crf/contract'
import ConditionEditor from './ConditionEditor.vue'
import { normalizeFieldForType, normalizePresentationForFieldType } from '@/lib/editor'

const props = defineProps<{
  field: ClinicCrfField
  presentation: ClinicCrfFieldPresentation
  fields: ClinicCrfField[]
}>()

const emit = defineEmits<{
  'update:field': [value: ClinicCrfField]
  'update:presentation': [value: ClinicCrfFieldPresentation]
  rename: [oldPath: string, newPath: string]
}>()

const typeLabels: Record<string, string> = {
  string: 'Krótki tekst', text: 'Długi tekst', integer: 'Liczba całkowita', number: 'Liczba dziesiętna',
  boolean: 'Tak / nie', date: 'Data', datetime: 'Data i czas', choice: 'Jednokrotny wybór',
  multichoice: 'Wielokrotny wybór', partial_date: 'Data częściowa',
}
const sourceLabels: Record<string, string> = { site: 'Ośrodek', subject: 'Uczestnik', device: 'Urządzenie', ehr: 'EHR', derived: 'Wyliczane' }
const missingLabels: Record<string, string> = { unknown: 'Nieznane', not_done: 'Nie wykonano', not_applicable: 'Nie dotyczy', withheld: 'Odmowa', other: 'Inne' }

const numeric = computed(() => ['integer', 'number'].includes(props.field.type))
const textual = computed(() => ['string', 'text'].includes(props.field.type))
const selectable = computed(() => ['choice', 'multichoice'].includes(props.field.type))

function update(patch: Partial<ClinicCrfField>): void {
  emit('update:field', { ...props.field, ...patch })
}

function updateType(type: ClinicCrfFieldType): void {
  emit('update:field', normalizeFieldForType(props.field, type))
  emit('update:presentation', normalizePresentationForFieldType(props.presentation, type))
}

function optionalNumber(event: Event): number | undefined {
  const raw = (event.target as HTMLInputElement).value
  return raw === '' ? undefined : Number(raw)
}

function updatePresentation(patch: Partial<ClinicCrfFieldPresentation>): void {
  emit('update:presentation', { ...props.presentation, ...patch })
}

function toggleMissing(code: ClinicCrfMissingCode, enabled: boolean): void {
  const current = props.field.missing_codes ?? []
  const next = enabled ? [...current, code] : current.filter((item) => item !== code)
  update({ missing_codes: next.length ? next : undefined, ...(next.length ? {} : { missing_reason_required: undefined }) })
}

function optionsText(): string {
  return (props.field.options ?? []).map((value) => {
    const label = props.presentation.option_labels?.[value]
    return label && label !== value ? `${value} | ${label}` : value
  }).join('\n')
}

function updateOptions(raw: string): void {
  const labels: Record<string, string> = {}
  const options = raw.split('\n').map((line) => line.trim()).filter(Boolean).map((line) => {
    const [valuePart, ...labelParts] = line.split('|')
    const value = valuePart?.trim() ?? ''
    const label = labelParts.join('|').trim()
    if (value && label) labels[value] = label
    return value
  }).filter(Boolean)
  update({ options })
  updatePresentation({ option_labels: Object.keys(labels).length ? labels : undefined })
}

function updateCondition(key: 'visible_when' | 'required_when', value: ClinicCrfCondition | undefined): void {
  const next = { ...props.field }
  if (value) next[key] = value
  else delete next[key]
  emit('update:field', next)
}
</script>

<template>
  <div class="field-editor-form">
    <section class="editor-section">
      <div class="section-heading"><div><span class="eyebrow">Tożsamość pola</span><h3>Treść i typ</h3></div></div>
      <div class="form-grid two-columns">
        <label class="span-2"><span>Etykieta widoczna dla użytkownika</span><input :value="field.label" maxlength="500" @input="update({ label: ($event.target as HTMLInputElement).value })" /></label>
        <label><span>Ścieżka w kontrakcie</span><input :value="field.path" maxlength="120" spellcheck="false" @change="emit('rename', field.path, ($event.target as HTMLInputElement).value.trim())" /><small>lower_snake_case, np. vitals.systolic</small></label>
        <label><span>Typ odpowiedzi</span><select :value="field.type" @change="updateType(($event.target as HTMLSelectElement).value as ClinicCrfFieldType)"><option v-for="type in FIELD_TYPES" :key="type" :value="type">{{ typeLabels[type] }}</option></select></label>
        <label><span>Źródło danych</span><select :value="field.source ?? ''" @change="update({ source: (($event.target as HTMLSelectElement).value || undefined) as ClinicCrfField['source'] })"><option value="">Nie wskazano</option><option v-for="source in FIELD_SOURCES" :key="source" :value="source">{{ sourceLabels[source] }}</option></select></label>
        <label><span>Szerokość w formularzu</span><select :value="presentation.width ?? 'full'" @change="updatePresentation({ width: ($event.target as HTMLSelectElement).value as 'full' | 'half' })"><option value="full">Pełna</option><option value="half">Połowa</option></select></label>
        <label class="check-row"><input type="checkbox" :checked="Boolean(field.required)" @change="update({ required: ($event.target as HTMLInputElement).checked || undefined })" /><span>Pole zawsze wymagane</span></label>
      </div>
    </section>

    <section class="editor-section">
      <div class="section-heading"><div><span class="eyebrow">Prezentacja</span><h3>Pomoc dla użytkownika</h3></div></div>
      <div class="form-grid two-columns">
        <label class="span-2"><span>Tekst pomocniczy</span><textarea :value="presentation.help_text ?? ''" rows="2" maxlength="2000" @input="updatePresentation({ help_text: ($event.target as HTMLTextAreaElement).value || undefined })"></textarea></label>
        <label class="span-2"><span>Placeholder</span><input :value="presentation.placeholder ?? ''" maxlength="2000" @input="updatePresentation({ placeholder: ($event.target as HTMLInputElement).value || undefined })" /></label>
      </div>
    </section>

    <section v-if="selectable" class="editor-section">
      <div class="section-heading"><div><span class="eyebrow">Słownik</span><h3>Opcje odpowiedzi</h3></div></div>
      <label><span>Jedna opcja w wierszu: <code>wartość | Etykieta</code></span><textarea :value="optionsText()" rows="6" spellcheck="false" @input="updateOptions(($event.target as HTMLTextAreaElement).value)"></textarea><small>Wartość trafia do kontraktu. Etykieta może być przyjaznym tłumaczeniem.</small></label>
    </section>

    <section v-if="numeric || textual" class="editor-section">
      <div class="section-heading"><div><span class="eyebrow">Kontrola jakości</span><h3>Ograniczenia wartości</h3></div></div>
      <div v-if="numeric" class="form-grid three-columns">
        <label><span>Minimum</span><input type="number" :value="field.min" @input="update({ min: optionalNumber($event) })" /></label>
        <label><span>Maksimum</span><input type="number" :value="field.max" @input="update({ max: optionalNumber($event) })" /></label>
        <label v-if="field.type === 'number'"><span>Miejsca po przecinku</span><input type="number" min="0" max="10" :value="field.precision" @input="update({ precision: optionalNumber($event) })" /></label>
        <label><span>Jednostka (np. UCUM)</span><input :value="field.unit ?? ''" maxlength="40" placeholder="np. kg, mm[Hg]" @input="update({ unit: ($event.target as HTMLInputElement).value || undefined })" /></label>
      </div>
      <div v-else class="form-grid two-columns">
        <label><span>Minimalna długość</span><input type="number" min="0" :value="field.min_length" @input="update({ min_length: optionalNumber($event) })" /></label>
        <label><span>Maksymalna długość</span><input type="number" min="1" :value="field.max_length" @input="update({ max_length: optionalNumber($event) })" /></label>
      </div>
    </section>

    <section class="editor-section">
      <div class="section-heading"><div><span class="eyebrow">Jawny brak danych</span><h3>Dozwolone kody missing</h3></div></div>
      <div class="choice-pills">
        <label v-for="code in MISSING_CODES" :key="code" :class="{ selected: field.missing_codes?.includes(code) }"><input type="checkbox" :checked="field.missing_codes?.includes(code)" @change="toggleMissing(code, ($event.target as HTMLInputElement).checked)" /><span>{{ missingLabels[code] }}</span></label>
      </div>
      <label v-if="field.missing_codes?.length" class="check-row top-gap"><input type="checkbox" :checked="Boolean(field.missing_reason_required)" @change="update({ missing_reason_required: ($event.target as HTMLInputElement).checked || undefined })" /><span>Wymagaj opisania powodu</span></label>
    </section>

    <section class="editor-section">
      <div class="section-heading"><div><span class="eyebrow">Logika formularza</span><h3>Warunki</h3></div></div>
      <ConditionEditor :model-value="field.visible_when" :fields="fields" title="Widoczność pola" hint="Pole pojawi się dopiero po spełnieniu warunku." @update:model-value="updateCondition('visible_when', $event)" />
      <ConditionEditor :model-value="field.required_when" :fields="fields" title="Warunkowe wymaganie" hint="Warunek uzupełnia ustawienie „zawsze wymagane”." @update:model-value="updateCondition('required_when', $event)" />
    </section>
  </div>
</template>
